/**
 * New Procurement Items Report — what a project still has to buy.
 *
 * An item that is not in stock and has not been transferred into the project has
 * to be procured. The quantities come from the project's most recent MRP run;
 * anything the project manager has already moved in is left out, because that is
 * material the project has rather than material it needs.
 */
import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import api from '../api/api';

const money = (n) => (n == null ? '' : Number(n).toLocaleString('en-IN', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
}));
const qty = (n) => (n == null ? '' : Number(n).toLocaleString('en-IN', {
  minimumFractionDigits: 0, maximumFractionDigits: 4,
}));
const asDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '—');

export default function NewProcurementItemsReport() {
  const [projectCode, setProjectCode] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const search = async (e) => {
    if (e) e.preventDefault();
    const code = projectCode.trim();
    if (!code) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.get(`/reports/new-procurement?project_code=${encodeURIComponent(code)}`);
      setReport(data);
      setSearched(true);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Could not generate the report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  /** The client's own layout: the selection above, then the numbered lines. */
  const exportExcel = () => {
    if (!report?.rows?.length) return;
    const sheet = [
      ['Selection Option', 'Project Code', report.project_code],
      [],
      ...(report.project ? [
        ['Project Name', report.project.project_name || ''],
        ['Sales Order', report.project.sales_order || '', 'Customer', report.project.customer || ''],
        ['Posting Date', asDate(report.project.posting_date), 'Delivery Date', asDate(report.project.delivery_date)],
        [],
      ] : []),
      // The client's format: these six columns, in this order.
      ['Sr.No.', 'Item Code', 'Item Description', 'Qty.', 'Rate', 'Total Value'],
      ...report.rows.map((r) => [
        r.sr_no, r.item_code, r.item_description, Number(r.required_qty),
        r.rate == null ? '' : Number(r.rate),
        r.total_value == null ? '' : Number(r.total_value),
      ]),
      [],
      ['', '', '', '', 'Total Purchase Value', Number(report.total_value || 0)],
    ];
    const ws = XLSX.utils.aoa_to_sheet(sheet);
    ws['!cols'] = [{ wch: 8 }, { wch: 26 }, { wch: 46 }, { wch: 12 }, { wch: 14 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Purchase Value');
    XLSX.writeFile(wb, `New Procurement Items Report - ${report.project_code}.xlsx`);
  };

  return (
    <div className="space-y-3 pb-6">
      <div className="text-2xs text-sap-textLight">
        Main Menu › Inventory › <span className="text-sap-text font-medium">New Procurement Items Report</span>
      </div>
      <h1 className="page-title">New Procurement Items Report</h1>

      <form onSubmit={search} className="bg-white rounded-2xl shadow-sm p-4">
        <label className="block text-2xs font-semibold text-sap-textLight uppercase tracking-wide mb-1">
          Project Code
        </label>
        <div className="flex items-center gap-2">
          <input
            value={projectCode}
            onChange={(e) => setProjectCode(e.target.value)}
            placeholder="e.g. 262701072"
            className="w-80 px-3 py-1.5 text-xs border border-sap-border rounded-lg
              focus:outline-none focus:ring-1 focus:ring-sap-blue"
          />
          <button type="submit" disabled={loading || !projectCode.trim()}
            className="px-6 py-1.5 text-xs font-semibold rounded-lg bg-sap-blue text-white
              hover:bg-sap-blueDark transition-colors disabled:opacity-40">
            {loading ? 'Searching…' : 'Search'}
          </button>
          <button type="button" onClick={exportExcel}
            disabled={!report?.rows?.length}
            title={report?.rows?.length
              ? 'Download this report as an Excel file'
              : 'Search a project code with items to procure first'}
            className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white
              hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            Export to Excel
          </button>
        </div>
        <p className="mt-1.5 text-2xs text-sap-textLight">
          Items neither in stock nor transferred into the project, from its latest MRP run.
        </p>
      </form>

      {error && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">{error}</div>}
      {report?.note && (
        <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
          {report.note}
        </div>
      )}
      {report?.price_warning && (
        <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
          {report.price_warning}
        </div>
      )}

      {!searched && !loading && (
        <div className="bg-white rounded-2xl shadow-sm py-16 text-center text-xs text-sap-textLight">
          Enter a project code to generate the New Procurement Items Report.
        </div>
      )}

      {report && (
        <>
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <p className="text-xs font-semibold text-sap-text mb-3">Project Information</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-xs">
              <Field label="Project Code" value={report.project?.project_code || report.project_code} />
              <Field label="Project Name" value={report.project?.project_name || '—'} />
              <Field label="Sales Order" value={report.project?.sales_order || '—'} highlight />
              <Field label="Customer" value={report.project?.customer || '—'} />
              <Field label="Posting Date" value={asDate(report.project?.posting_date)} />
              <Field label="Delivery Date" value={asDate(report.project?.delivery_date)} />
              <Field label="Status" value={report.project?.status || '—'} />
              <Field label="Planned By" value={report.run ? `Run #${report.run.run_id}` : '—'} />
              <Field label="Planned On" value={report.run ? asDate(report.run.planned_at) : '—'} />
              <Field label="Generated By" value={report.generated_by || '—'} />
            </div>
            {report.run?.counted_warehouses_known && (
              <p className="mt-3 text-2xs text-sap-text">
                Stock counted from warehouse{report.run.counted_warehouses.length === 1 ? '' : 's'}{' '}
                <strong>{report.run.counted_warehouses.join(', ')}</strong>. Stock held anywhere else was
                not counted by this run, the same way SAP ignores it.
              </p>
            )}
            {report.excluded_transferred > 0 && (
              <p className="mt-3 text-2xs text-sap-textLight">
                {report.excluded_transferred} item{report.excluded_transferred === 1 ? '' : 's'} already
                transferred into this project {report.excluded_transferred === 1 ? 'is' : 'are'} left out.
              </p>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-sap-bg border-b border-sap-border">
                <tr className="text-sap-textLight">
                  <th className="text-left px-3 py-2 font-semibold w-14">Sr.No.</th>
                  <th className="text-left px-3 py-2 font-semibold">Item Code</th>
                  <th className="text-left px-3 py-2 font-semibold">Item Description</th>
                  <th className="text-right px-3 py-2 font-semibold">Required Qty.</th>
                  <th className="text-right px-3 py-2 font-semibold" title="Stock free of commitments in the warehouses this run counted">
                    Free Stock
                  </th>
                  <th className="text-right px-3 py-2 font-semibold" title="Quantity this run is moving in from those warehouses">
                    Transfer
                  </th>
                  <th className="text-right px-3 py-2 font-semibold">Rate</th>
                  <th className="text-right px-3 py-2 font-semibold">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-10 text-center text-sap-textLight italic">
                      Nothing to procure for project {report.project_code}.
                    </td>
                  </tr>
                ) : report.rows.map((r) => (
                  <tr key={r.item_code} className="border-t border-sap-border/30">
                    <td className="px-3 py-2 text-sap-textLight">{r.sr_no}</td>
                    <td className="px-3 py-2 font-medium text-sap-blue">{r.item_code}</td>
                    <td className="px-3 py-2 text-sap-text">{r.item_description}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{qty(r.required_qty)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-sap-textLight">
                      {r.counted_free_stock == null ? '—' : qty(r.counted_free_stock)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-sap-textLight">
                      {r.transfer_qty ? qty(r.transfer_qty) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums" title={r.rate_source || ''}>
                      {r.rate == null ? '—' : `₹${money(r.rate)}`}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">
                      {r.total_value == null ? '—' : `₹${money(r.total_value)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
              {report.rows.length > 0 && (
                <tfoot>
                  <tr className="border-t border-sap-border bg-sap-bg/60">
                    <td colSpan={7} className="px-3 py-2 text-right font-semibold text-sap-text">
                      TOTAL PURCHASE VALUE:
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-sap-blueDeeper tabular-nums">
                      ₹{money(report.total_value)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, value, highlight }) {
  return (
    <div>
      <p className="text-2xs text-sap-textLight uppercase tracking-wide">{label}</p>
      <p className={`mt-0.5 ${highlight ? 'text-sap-blue font-medium' : 'text-sap-text'}`}>{value}</p>
    </div>
  );
}
